import { atom } from 'jotai';
import type { Getter, Setter } from 'jotai';
import type { Config } from 'atoms/Rtool/newConfigAtoms';
import type { Powertrain } from 'atoms/powertrainAtom';
import { authTokenAtom } from 'atoms/authAtoms';
import { refreshAuthSession } from 'api/fetchAuthSession';
import { ApiError } from 'api/apiError';
import {
    fetchCopyJobStatus,
    fetchLatestCopyJob,
    isTerminalCopyJobStatus,
    type CopyJobSnapshot,
} from 'api/copyJobs';

// 計算セット丸ごとコピー非同期化（issue #41/#42）のジョブ監視基盤。
// ポーラーは React コンポーネントに紐付けない: write atom が受け取る get/set をクロージャで
// 保持して非同期ループを継続させる（画面遷移・アンマウントと無関係に動く）。getDefaultStore() は
// 使わない（createStore() によるテストのストア分離を保つため）。

export type CopyJobEntryErrorReason = 'not_found' | 'auth' | 'unexpected';
export type CopyJobWatchState = 'watching' | 'terminal' | 'error';

// terminal到達時のみ意味を持つ（#45）。'live': このブラウザセッションが監視していたループ自身が
// 遷移を検知した（進捗→結果表示への自動切替の対象）。'restored': jobs/latestのcold restore
// （リロード直後・初回訪問等、このセッションで一度も監視していなかった）で既に終端の結果を取得した
// （前回の結果。自動で開かず導線のみ出す対象）。
export type CopyJobResultOrigin = 'live' | 'restored';

export type CopyJobEntry = {
    destinationKey: string;
    powertrain: Powertrain;
    config: Config;
    jobId: string;
    statusUrl: string;
    snapshot: CopyJobSnapshot | null;
    watchState: CopyJobWatchState;
    errorReason?: CopyJobEntryErrorReason;
    resultOrigin?: CopyJobResultOrigin;
};

// コピー先（powertrain + 6ID）で複数ジョブを同時に取り違えずに保持する（issue #42 AC7）。
export const copyJobsAtom = atom<Record<string, CopyJobEntry>>({});

// recentCalcsAtom.ts の identityKey と同型。#43/#44/#45 が同じキーを独立に導出できるよう公開する。
export const buildDestinationKey = (powertrain: Powertrain, config: Config): string =>
    [
        powertrain,
        config.project_id,
        config.destination_id,
        config.lot_id,
        config.phase_id,
        config.unit_id,
        config.calculation_id,
    ].join('|');

// 終端遷移の通知イベント。snackbarQueueAtom と同型の push→consume キュー。
// #45（結果表示・完了通知）が消費する。ループ自身が観測した非終端→終端の遷移時のみ push する
// （restore/seed 経路からは push しない。#45 が画面を開くたびに古い完了を再通知してしまうため）。
export type CopyJobTerminalEvent = {
    id: number;
    destinationKey: string;
    snapshot: CopyJobSnapshot;
};
export const copyJobTerminalEventsAtom = atom<CopyJobTerminalEvent[]>([]);

let nextTerminalEventId = 0;

// ---- ポーリング間隔・エラー分類（純粋関数。テストのため export する） ----

export const DEFAULT_POLL_INTERVAL_SECONDS = 3;
export const MIN_POLL_INTERVAL_MS = 3000;
export const MAX_POLL_INTERVAL_MS = 10000;
export const HIDDEN_POLL_INTERVAL_MS = 30000;
export const ERROR_BACKOFF_BASE_MS = 2000;
export const ERROR_BACKOFF_MAX_MS = 30000;
export const MAX_CONSECUTIVE_AUTH_ERRORS = 3;
export const RESTORE_MAX_ATTEMPTS = 5;

// happy path: 3秒起点で10秒まで徐々に緩める（issue #42 §2.2 要件2）。
export const nextHappyPathDelayMs = (currentMs: number): number =>
    Math.min(currentMs + 1000, MAX_POLL_INTERVAL_MS);

// エラー時は指数バックオフで再試行し続ける（諦めない。issue #42 §2.2 要件4・エラー分類表）。
export const errorBackoffDelayMs = (consecutiveErrors: number): number =>
    Math.min(ERROR_BACKOFF_BASE_MS * 2 ** Math.max(0, consecutiveErrors - 1), ERROR_BACKOFF_MAX_MS);

// タブ非アクティブなら間隔を大きく延ばす（issue #42 §2.2 要件3）。DOM非依存の純粋関数。
export const effectiveDelayMs = (baseMs: number, hidden: boolean): number =>
    hidden ? Math.max(baseMs, HIDDEN_POLL_INTERVAL_MS) : baseMs;

export type PollErrorAction = 'retry' | 'refresh-and-retry' | 'restore-fallback' | 'fatal';

// issue #42 §2.2 のエラー分類表そのもの。ApiError でない（ネットワーク例外）は retry。
export const classifyPollError = (error: unknown): PollErrorAction => {
    if (error instanceof ApiError) {
        if (error.status === 404) return 'restore-fallback';
        if (error.status === 401) return 'refresh-and-retry';
        if (error.status === 429 || error.status >= 500) return 'retry';
        return 'fatal';
    }
    return 'retry';
};

// ---- sleep（setInterval不使用。逐次ループが自身の delay を await する） ----

// allowEarlyWake: タブ復帰による早期解決を許すか。happy-pathのsleepのみ許可し、エラーバックオフの
// sleepは許可しない（許すとタブ切替を繰り返すだけでバックオフが無効化され障害中のエンドポイントを
// 連打してしまう。/code-review xhigh 所見）。テスト注入のSleepFn（即時解決）は第2引数を無視してよい。
export type SleepFn = (ms: number, opts?: { allowEarlyWake?: boolean }) => Promise<void>;

const wakeResolvers = new Set<() => void>();

// タブが表示に復帰した瞬間、sleep 中の全ループを早期解決する（issue #42 §2.2 要件3）。
// 複数ループが同時に早期解決されても実害は軽微な追加リクエストのみのため、宛先単位に絞らない。
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            wakeResolvers.forEach((wake) => wake());
        }
    });
}

const isTabHidden = (): boolean => typeof document !== 'undefined' && document.hidden === true;

const defaultSleep: SleepFn = (ms, opts = {}) => {
    const allowEarlyWake = opts.allowEarlyWake ?? true;
    return new Promise((resolve) => {
        let timer: ReturnType<typeof setTimeout>;
        const wake = () => {
            clearTimeout(timer);
            wakeResolvers.delete(wake);
            resolve();
        };
        timer = setTimeout(wake, ms);
        if (allowEarlyWake) wakeResolvers.add(wake);
    });
};

export type CopyJobLoopDeps = { sleep?: SleepFn };

// ---- 認証リフレッシュ（single-flight） ----

let inFlightRefresh: Promise<string> | null = null;

// 複数コピー先が同時に401しても Amplify への forceRefresh 呼び出しを1回に集約する。
const refreshOnce = (set: Setter): Promise<string> => {
    if (!inFlightRefresh) {
        inFlightRefresh = refreshAuthSession(set).finally(() => {
            inFlightRefresh = null;
        });
    }
    return inFlightRefresh;
};

// ---- dedup / 世代管理（非公開の Jotai atom。createStore() ごとに自動的に分離される） ----

type PollRegistryEntry = { jobId: string; generation: number; alive: boolean };
const pollRegistryAtom = atom<Record<string, PollRegistryEntry>>({});

// 同じ destinationKey・同じ jobId の開始要求が alive な間に重複しても新しいループを起動しない
// （issue #42 AC5）。alive:false（フォールバック等で明示的に降ろした後）なら同じ jobId でも
// 新しい世代として再開できる（404フォールバックが同じ job_id を返すケースを dedup で潰さないため）。
const claimGeneration = (
    get: Getter,
    set: Setter,
    destinationKey: string,
    jobId: string,
): { generation: number; isDuplicate: boolean } => {
    const registry = get(pollRegistryAtom);
    const existing = registry[destinationKey];
    if (existing && existing.jobId === jobId && existing.alive) {
        return { generation: existing.generation, isDuplicate: true };
    }
    const generation = (existing?.generation ?? 0) + 1;
    set(pollRegistryAtom, { ...registry, [destinationKey]: { jobId, generation, alive: true } });
    return { generation, isDuplicate: false };
};

// 自分の世代がまだ現行かどうか（=より新しい開始要求に取って代わられていないか）を確認する。
// ループは await の直後に必ずこれを確認し、超過していれば store へ書き込まず即終了する。
const isCurrentGeneration = (get: Getter, destinationKey: string, generation: number): boolean =>
    get(pollRegistryAtom)[destinationKey]?.generation === generation;

// 監視を終える（このループはもう GET しない）ことを登録する。すでに別世代に置き換わっていれば
// 何もしない（新しい方を壊さない）。
const markDead = (get: Getter, set: Setter, destinationKey: string, generation: number): void => {
    const registry = get(pollRegistryAtom);
    const existing = registry[destinationKey];
    if (!existing || existing.generation !== generation) return;
    set(pollRegistryAtom, { ...registry, [destinationKey]: { ...existing, alive: false } });
};

// 確定した終端状態（jobs/latest からの復元等）を書き込む前に、そのdestinationKeyに対して
// まだaliveなポーリング世代があれば無効化する。世代番号を進めてalive:falseにすることで、
// 起床済みの旧ループがisCurrentGenerationチェックで自ら停止し、確定済みの終端表示を
// 後から上書きしないようにする（/code-review xhigh 所見: claimGeneration/markDead欠落）。
const supersedeAndRetire = (get: Getter, set: Setter, destinationKey: string, jobId: string): void => {
    const registry = get(pollRegistryAtom);
    const existing = registry[destinationKey];
    const generation = (existing?.generation ?? 0) + 1;
    set(pollRegistryAtom, { ...registry, [destinationKey]: { jobId, generation, alive: false } });
};

// ---- ストア書き込みヘルパー ----

const writeEntry = (set: Setter, destinationKey: string, entry: CopyJobEntry): void => {
    set(copyJobsAtom, (prev) => ({ ...prev, [destinationKey]: entry }));
};

const updateEntryError = (
    set: Setter,
    destinationKey: string,
    errorReason: CopyJobEntryErrorReason,
): void => {
    set(copyJobsAtom, (prev) => {
        const existing = prev[destinationKey];
        if (!existing) return prev;
        return { ...prev, [destinationKey]: { ...existing, watchState: 'error', errorReason } };
    });
};

const pushTerminalEvent = (set: Setter, destinationKey: string, snapshot: CopyJobSnapshot): void => {
    set(copyJobTerminalEventsAtom, (prev) => [
        ...prev,
        { id: nextTerminalEventId++, destinationKey, snapshot },
    ]);
};

// ---- ポーリングループ本体 ----

type PollLoopContext = {
    destinationKey: string;
    powertrain: Powertrain;
    config: Config;
    jobId: string;
    statusUrl: string;
    generation: number;
};

const runPollLoop = async (
    get: Getter,
    set: Setter,
    ctx: PollLoopContext,
    initialPollIntervalSeconds: number | undefined,
    deps: CopyJobLoopDeps,
    initialConsecutiveErrors = 0,
): Promise<void> => {
    const sleep = deps.sleep ?? defaultSleep;
    let delayMs = Math.max(
        (initialPollIntervalSeconds ?? DEFAULT_POLL_INTERVAL_SECONDS) * 1000,
        MIN_POLL_INTERVAL_MS,
    );
    let consecutiveErrors = 0;
    let consecutive401 = 0;

    // 404フォールバックからの引き継ぎ（同じジョブが即座に再404するケース）で、間隔を0に
    // リセットせず前回の失敗回数分のバックオフを1回はさむ（/code-review xhigh 所見: リクエストストーム対策）。
    if (initialConsecutiveErrors > 0) {
        if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;
        await sleep(effectiveDelayMs(errorBackoffDelayMs(initialConsecutiveErrors), isTabHidden()), {
            allowEarlyWake: false,
        });
    }

    while (true) {
        if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;

        let lastCycleWasError = false;

        try {
            const authToken = get(authTokenAtom);
            const snapshot = await fetchCopyJobStatus(authToken, ctx.statusUrl);
            if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;

            // バックオフで伸びていた間隔は、エラーから復帰した直後だけ起点(3秒)に戻す。
            // 通常の（エラーを挟まない）happy-path進行では緩め続ける（issue #42 §2.2 要件2）。
            const recoveredFromError = consecutiveErrors > 0 || consecutive401 > 0;
            consecutiveErrors = 0;
            consecutive401 = 0;
            if (recoveredFromError) {
                delayMs = Math.max(
                    (initialPollIntervalSeconds ?? DEFAULT_POLL_INTERVAL_SECONDS) * 1000,
                    MIN_POLL_INTERVAL_MS,
                );
            }

            const previous = get(copyJobsAtom)[ctx.destinationKey]?.snapshot ?? null;
            const watchState: CopyJobWatchState = isTerminalCopyJobStatus(snapshot.status)
                ? 'terminal'
                : 'watching';
            writeEntry(set, ctx.destinationKey, {
                destinationKey: ctx.destinationKey,
                powertrain: ctx.powertrain,
                config: ctx.config,
                jobId: ctx.jobId,
                statusUrl: ctx.statusUrl,
                snapshot,
                watchState,
                // このループ自身がalive（=このセッションで監視中）な状態で検知した遷移なので'live'（#45）。
                resultOrigin: watchState === 'terminal' ? 'live' : undefined,
            });

            if (watchState === 'terminal') {
                markDead(get, set, ctx.destinationKey, ctx.generation);
                // 既に終端だったジョブの再確認（復元直後の1回目GET等）では再発火しない。
                if (!previous || !isTerminalCopyJobStatus(previous.status)) {
                    pushTerminalEvent(set, ctx.destinationKey, snapshot);
                }
                return;
            }

            delayMs = nextHappyPathDelayMs(delayMs);
        } catch (error) {
            if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;

            const action = classifyPollError(error);

            if (action === 'restore-fallback') {
                // 監視を打ち切り、jobs/latest で取り直す。alive を先に降ろしてから引き渡すことで、
                // 同じ job_id が返ってきても dedup に誤って潰されないようにする。
                markDead(get, set, ctx.destinationKey, ctx.generation);

                // jobs/latest 自体が5xx/ネットワーク例外/401で失敗した場合も「諦めない」方針を
                // 適用する（404によるnullとは区別する。/code-review xhigh 所見）。
                while (true) {
                    if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;
                    try {
                        const fallback = await fetchLatestCopyJob(
                            get(authTokenAtom),
                            ctx.powertrain,
                            ctx.config,
                        );
                        if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;

                        if (fallback) {
                            // jotai の write atom は「自分自身への set」を再帰呼び出しではなく
                            // 自己代入として扱うため、beginWatchingCopyJobAtom を set() 経由で
                            // 呼ぶとここでは writer が再実行されない。共通ロジックは plain
                            // function（seedAndWatch）として直接呼ぶ。
                            seedAndWatch(
                                get,
                                set,
                                {
                                    powertrain: ctx.powertrain,
                                    config: ctx.config,
                                    jobId: fallback.job_id,
                                    statusUrl: fallback.status_url,
                                    seedSnapshot: fallback,
                                    // 同じジョブがすぐ再404するケースでも間隔0で連打しないよう、
                                    // このサイクルの失敗回数を新しいループへ引き継ぐ。
                                    initialConsecutiveErrors: consecutiveErrors + 1,
                                    // このハンドオフに入った時点で直前まで実際にこのdestinationKeyを
                                    // アクティブに監視していた（=真に「たった今」判明した。issue #45）。
                                    terminalResultOrigin: 'live',
                                },
                                deps,
                            );
                        } else {
                            // それも404なら異常（issue #42 §2.2 エラー分類表）。
                            updateEntryError(set, ctx.destinationKey, 'not_found');
                        }
                        return;
                    } catch (fallbackError) {
                        const fallbackAction = classifyPollError(fallbackError);

                        if (fallbackAction === 'fatal') {
                            updateEntryError(set, ctx.destinationKey, 'unexpected');
                            return;
                        }

                        if (fallbackAction === 'refresh-and-retry') {
                            consecutive401 += 1;
                            if (consecutive401 >= MAX_CONSECUTIVE_AUTH_ERRORS) {
                                updateEntryError(set, ctx.destinationKey, 'auth');
                                return;
                            }
                            await refreshOnce(set).catch(() => undefined);
                        } else {
                            consecutive401 = 0;
                        }

                        consecutiveErrors += 1;
                        delayMs = errorBackoffDelayMs(consecutiveErrors);

                        if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;
                        await sleep(effectiveDelayMs(delayMs, isTabHidden()), { allowEarlyWake: false });
                    }
                }
            }

            if (action === 'fatal') {
                markDead(get, set, ctx.destinationKey, ctx.generation);
                updateEntryError(set, ctx.destinationKey, 'unexpected');
                return;
            }

            if (action === 'refresh-and-retry') {
                consecutive401 += 1;
                if (consecutive401 >= MAX_CONSECUTIVE_AUTH_ERRORS) {
                    // issue原文は無限リトライを想定するが、死んだリフレッシュトークンへの無限リトライは
                    // 有害なため上限を設ける（advisorレビュー判断。execplan §4/§9で明示）。
                    markDead(get, set, ctx.destinationKey, ctx.generation);
                    updateEntryError(set, ctx.destinationKey, 'auth');
                    return;
                }
                await refreshOnce(set).catch(() => undefined);
            } else {
                // 401以外のエラーを挟むと「連続」性が切れる（/code-review xhigh 所見）。
                consecutive401 = 0;
            }

            consecutiveErrors += 1;
            delayMs = errorBackoffDelayMs(consecutiveErrors);
            lastCycleWasError = true;
        }

        if (!isCurrentGeneration(get, ctx.destinationKey, ctx.generation)) return;
        await sleep(effectiveDelayMs(delayMs, isTabHidden()), { allowEarlyWake: !lastCycleWasError });
    }
};

// ---- 公開 write atom ----

export type BeginWatchingCopyJobParams = {
    powertrain: Powertrain;
    config: Config;
    jobId: string;
    statusUrl: string;
    seedSnapshot: CopyJobSnapshot | null;
    pollIntervalSeconds?: number;
    // 404フォールバックの引き継ぎ専用（runPollLoop内部が指定する）。省略時は通常のstart/restoreと
    // 同様、間隔0からの即時ポーリングになる。
    initialConsecutiveErrors?: number;
    // seedSnapshotが既にterminalな場合のresultOrigin（issue #45）。呼び出し元が「今まさにライブで
    // 判明した」（404フォールバック・409直後合流）か「静的な復元で分かった」（jobs/latestによる
    // 画面マウント時の復元）かを明示する。省略時は'restored'（cold restore側の既定）。
    // pollRegistryAtomの有無（wasAlreadyTracked）から推測しないのは、この分岐自身が呼ぶ
    // supersedeAndRetireがレジストリを書き込むため、2回目以降の呼び出しで
    // 誤って'live'と判定されてしまうため（/code-review xhigh所見）。
    terminalResultOrigin?: CopyJobResultOrigin;
};

// start/restore/フォールバックが共通で使う内部ロジック。plain function にする理由:
// jotai の write atom は「自分自身への set(sameAtom, ...)」を再帰呼び出しではなく自己代入として
// 扱う（setter は生成元の atom に閉じているため）。runPollLoop のフォールバック handoff は
// beginWatchingCopyJobAtom の writer 自身が渡した get/set をクロージャで保持しているため、
// そこから set(beginWatchingCopyJobAtom, ...) を呼んでも writer は再実行されない。
// そのため実処理は atom の外の plain function に置き、全エントリポイントがこれを直接呼ぶ。
// seedSnapshot が既に終端なら、ループを起動せずそのまま結果として保持する
// （jobs/latest が既に completed/failed のジョブを返すケースを想定。終端イベントは push しない）。
const seedAndWatch = (
    get: Getter,
    set: Setter,
    params: BeginWatchingCopyJobParams,
    deps: CopyJobLoopDeps,
): void => {
    const destinationKey = buildDestinationKey(params.powertrain, params.config);

    if (params.seedSnapshot && isTerminalCopyJobStatus(params.seedSnapshot.status)) {
        // このセッション内で既に（生存中か否かに関わらず）このdestinationKeyを追跡していたか。
        // 404フォールバック経由の終端遷移はここが true になる（フォールバック直前にmarkDeadで
        // レジストリへ書いているため）。true の場合のみ終端通知を発火対象にする。
        // cold restore（リロード直後・初回訪問等でレジストリが空）は false になり通知しない
        // （#45が画面を開くたびに何日も前の完了を再通知してしまうのを防ぐ、という元々の設計意図どおり）。
        const wasAlreadyTracked = get(pollRegistryAtom)[destinationKey] !== undefined;

        // 確定した終端状態を書き込む前に、まだaliveな旧世代（フォールバック元や別画面が
        // 起動したループ）を無効化する。しないと、後で起床した旧ループがこの確定表示を
        // エラー表示等で上書きしてしまう（/code-review xhigh 所見）。
        supersedeAndRetire(get, set, destinationKey, params.jobId);

        const previous = get(copyJobsAtom)[destinationKey]?.snapshot ?? null;
        writeEntry(set, destinationKey, {
            destinationKey,
            powertrain: params.powertrain,
            config: params.config,
            jobId: params.jobId,
            statusUrl: params.statusUrl,
            snapshot: params.seedSnapshot,
            watchState: 'terminal',
            resultOrigin: params.terminalResultOrigin ?? 'restored',
        });
        // 同一セッション内で追跡していた場合に限り、直前まで非終端（またはエントリ無し）
        // だったときだけ終端通知を発火する（二重発火防止・/code-review xhigh 所見）。
        if (wasAlreadyTracked && (!previous || !isTerminalCopyJobStatus(previous.status))) {
            pushTerminalEvent(set, destinationKey, params.seedSnapshot);
        }
        return;
    }

    const { generation, isDuplicate } = claimGeneration(get, set, destinationKey, params.jobId);
    if (isDuplicate) {
        if (params.seedSnapshot) {
            set(copyJobsAtom, (prev) => {
                const existing = prev[destinationKey];
                if (!existing) return prev;
                return { ...prev, [destinationKey]: { ...existing, snapshot: params.seedSnapshot } };
            });
        }
        return;
    }

    writeEntry(set, destinationKey, {
        destinationKey,
        powertrain: params.powertrain,
        config: params.config,
        jobId: params.jobId,
        statusUrl: params.statusUrl,
        snapshot: params.seedSnapshot,
        watchState: 'watching',
    });

    void runPollLoop(
        get,
        set,
        {
            destinationKey,
            powertrain: params.powertrain,
            config: params.config,
            jobId: params.jobId,
            statusUrl: params.statusUrl,
            generation,
        },
        params.pollIntervalSeconds,
        deps,
        params.initialConsecutiveErrors ?? 0,
    );
};

// #43/#44 等の外部からは通常の write atom として呼べる（store.set(beginWatchingCopyJobAtom, ...)）。
export const beginWatchingCopyJobAtom = atom(
    null,
    (get: Getter, set: Setter, params: BeginWatchingCopyJobParams, deps: CopyJobLoopDeps = {}) => {
        seedAndWatch(get, set, params, deps);
    },
);

export type StartCopyJobParams = {
    powertrain: Powertrain;
    config: Config;
    jobId: string;
    statusUrl: string;
    pollIntervalSeconds?: number;
};

// #43 が POST 202 の直後に呼ぶ。まだ snapshot が無い状態（queued 直後）から監視を始める。
export const startCopyJobAtom = atom(
    null,
    (get: Getter, set: Setter, params: StartCopyJobParams, deps: CopyJobLoopDeps = {}) => {
        seedAndWatch(get, set, { ...params, seedSnapshot: null }, deps);
    },
);

export type RestoreLatestCopyJobParams = {
    powertrain: Powertrain;
    config: Config;
};

// #43/#44 が画面マウント時に呼ぶ。404（実績なし）は正常応答として何もしない（issue #42 AC6）。
// authTokenAtom がまだ空（リロード直後の認証未確定）な間の401は、classifyPollError の
// refresh-and-retry 分岐を再利用してここでも吸収する（issue #42 AC2）。
export const restoreLatestCopyJobAtom = atom(
    null,
    async (
        get: Getter,
        set: Setter,
        params: RestoreLatestCopyJobParams,
        deps: CopyJobLoopDeps = {},
    ) => {
        const sleep = deps.sleep ?? defaultSleep;
        const destinationKey = buildDestinationKey(params.powertrain, params.config);
        // issue #46: この復元呼び出しを開始した時点の世代を記録する。応答受信時に世代が
        // 変わっていれば、この復元がリクエストを出した後に別のclaim（新規コピー開始・
        // 404フォールバックの引き継ぎ等）が発生したことを意味する（isCurrentGenerationと
        // 同じ因果関係の考え方）。'alive'（今生きているか）だけを見ると、その新しいジョブが
        // 復元の応答を待つ間に自ら終端に達した（alive:falseになった）瞬間に判定が効かなくなり、
        // 遅れて届いた古い別ジョブの応答が正しい終端表示を上書きしてしまう（/code-review所見）。
        const generationAtStart = get(pollRegistryAtom)[destinationKey]?.generation;

        for (let attempt = 0; attempt < RESTORE_MAX_ATTEMPTS; attempt += 1) {
            try {
                const authToken = get(authTokenAtom);
                const snapshot = await fetchLatestCopyJob(authToken, params.powertrain, params.config);
                if (!snapshot) return;

                // 復元の応答を待つ間に別ジョブが新しくclaimされ、その別ジョブとは異なるジョブを
                // この復元が報告しているなら、復元は完全にno-opとする（生存中か否かに関わらず、
                // 上書きしない。一方、復元開始時から世代が変わっていなければ、復元が報告する
                // 別ジョブは復元と無関係に以前から存在していた正当な発見のため上書きを許す）。
                const tracked = get(pollRegistryAtom)[destinationKey];
                if (tracked && tracked.generation !== generationAtStart && tracked.jobId !== snapshot.job_id) {
                    return;
                }

                seedAndWatch(
                    get,
                    set,
                    {
                        powertrain: params.powertrain,
                        config: params.config,
                        jobId: snapshot.job_id,
                        statusUrl: snapshot.status_url,
                        seedSnapshot: snapshot,
                    },
                    deps,
                );
                return;
            } catch (error) {
                const action = classifyPollError(error);
                const isLastAttempt = attempt === RESTORE_MAX_ATTEMPTS - 1;

                if (action === 'refresh-and-retry' && !isLastAttempt) {
                    await refreshOnce(set).catch(() => undefined);
                    // runPollLoopの同種分岐と対称に、リフレッシュ後も間隔0で連打しない
                    // （/code-review xhigh 所見）。
                    await sleep(errorBackoffDelayMs(attempt + 1), { allowEarlyWake: false });
                    continue;
                }
                if (action === 'retry' && !isLastAttempt) {
                    await sleep(errorBackoffDelayMs(attempt + 1), { allowEarlyWake: false });
                    continue;
                }
                // fatal、または再試行を使い切った。復元は静かに諦める
                // （AC6と同じ方針でエラー表示はしない。次の新規コピー開始時に発覚する）。
                return;
            }
        }
    },
);
